export type QuickNavigationEdge = 'top' | 'right' | 'bottom' | 'left';
export interface QuickNavigationPosition {edge: QuickNavigationEdge; ratio: number;}
export interface NavigationBounds {left: number; top: number; width: number; height: number;}
export interface NavigationPoint {x: number; y: number;}

export const DEFAULT_QUICK_NAVIGATION_POSITION: QuickNavigationPosition = {edge: 'right', ratio: 0.5};
export const QUICK_NAVIGATION_EDGES: QuickNavigationEdge[] = ['top', 'right', 'bottom', 'left'];

export function clampPosition(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

export function isHorizontalEdge(edge: QuickNavigationEdge): boolean {
  return edge === 'top' || edge === 'bottom';
}

export function readQuickNavigationPosition(value: unknown): QuickNavigationPosition | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<QuickNavigationPosition>;
  if (!QUICK_NAVIGATION_EDGES.includes(candidate.edge as QuickNavigationEdge)
    || typeof candidate.ratio !== 'number' || !Number.isFinite(candidate.ratio)
    || candidate.ratio < 0 || candidate.ratio > 1) return null;
  return {edge: candidate.edge as QuickNavigationEdge, ratio: candidate.ratio};
}

export function nearestNavigationPosition(point: NavigationPoint, bounds: NavigationBounds): QuickNavigationPosition {
  const distances = [
    Math.abs(point.y - bounds.top), Math.abs(point.x - bounds.left - bounds.width),
    Math.abs(point.y - bounds.top - bounds.height), Math.abs(point.x - bounds.left),
  ];
  const edge = QUICK_NAVIGATION_EDGES[distances.indexOf(Math.min(...distances))];
  const horizontal = isHorizontalEdge(edge);
  const length = horizontal ? bounds.width : bounds.height;
  const offset = horizontal ? point.x - bounds.left : point.y - bounds.top;
  return {edge, ratio: clampPosition((offset - 28) / Math.max(1, length - 56), 0, 1)};
}

export function navigationHandlePoint(position: QuickNavigationPosition, bounds: NavigationBounds, depth = 44): NavigationPoint {
  const horizontal = isHorizontalEdge(position.edge);
  const width = horizontal ? 56 : depth;
  const height = horizontal ? depth : 56;
  const x = bounds.left + (bounds.width - width) * position.ratio;
  const y = bounds.top + (bounds.height - height) * position.ratio;
  switch (position.edge) {
    case 'top': return {x, y: bounds.top};
    case 'bottom': return {x, y: bounds.top + bounds.height - height};
    case 'left': return {x: bounds.left, y};
    case 'right': return {x: bounds.left + bounds.width - width, y};
  }
}

export function navigationPanelPoint(
  position: QuickNavigationPosition, bounds: NavigationBounds, size: {width: number; height: number},
): NavigationPoint {
  const handle = navigationHandlePoint(position, bounds);
  let x = handle.x + 28 - size.width / 2;
  let y = handle.y + 28 - size.height / 2;
  switch (position.edge) {
    case 'top': y = bounds.top; break;
    case 'bottom': y = bounds.top + bounds.height - size.height; break;
    case 'left': x = bounds.left; break;
    case 'right': x = bounds.left + bounds.width - size.width; break;
  }
  return {
    x: clampPosition(x, bounds.left, bounds.left + bounds.width - size.width),
    y: clampPosition(y, bounds.top, bounds.top + bounds.height - size.height),
  };
}
